<script setup>
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import format from 'date-fns/format';
import addDays from 'date-fns/addDays';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { useStore } from 'vuex';
import types from 'dashboard/store/mutation-types';
import ConversationApi from 'dashboard/api/inbox/conversation';
import TaskComposer from 'dashboard/components/widgets/conversation/TaskComposer.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  variant: {
    type: String,
    default: 'panel',
  },
});

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();

const currentAccountId = useMapGetter('getCurrentAccountId');
const agentList = useMapGetter('agents/getAgents');
const tasks = ref([]);
const isLoading = ref(false);
const isInline = computed(() => props.variant === 'inline');

const confirmDialog = useTemplateRef('confirmDialog');
const isEditModalOpen = ref(false);
const isSavingEdit = ref(false);
const editingTask = ref(null);
const editDueAt = ref(null);
const editAssignee = ref(null);
const editNote = ref('');
const canSaveEdit = computed(
  () => !!editDueAt.value && !!editNote.value.trim() && !isSavingEdit.value
);

const fetchTasks = async () => {
  isLoading.value = true;
  try {
    const { data } = await ConversationApi.getTasks({
      date: new Date().toISOString(),
    });
    tasks.value = data.payload || [];
  } catch (error) {
    useAlert(t('CHAT_LIST.TODAY_TASKS.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openConversation = conversation => {
  const path = frontendURL(
    conversationUrl({
      accountId: currentAccountId.value,
      activeInbox: conversation.inbox_id,
      id: conversation.id,
    }),
    {
      ...route.query,
      assigneeType: route.query.assigneeType || 'me',
    }
  );

  router.push(path);
};

const formatTaskTime = dueAt => {
  if (!dueAt) return '';

  const date = new Date(dueAt);
  const isToday =
    format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
  return isToday ? format(date, 'HH:mm') : format(date, 'dd/MM HH:mm');
};

const isOverdueTask = dueAt => {
  if (!dueAt) return false;

  return new Date(dueAt) < new Date();
};

const completeTask = async task => {
  try {
    const { data } = await ConversationApi.completeTask(task.conversation.id);
    store.commit(types.UPDATE_CONVERSATION, data);
    await fetchTasks();
    emitter.emit('conversation:tasks_updated');
  } catch (error) {
    useAlert(t('CHAT_LIST.TODAY_TASKS.COMPLETE_ERROR'));
  }
};

const openEditTask = task => {
  editingTask.value = task;
  editDueAt.value = task.due_at
    ? new Date(task.due_at)
    : addDays(new Date(), 1);
  editAssignee.value = task.assignee
    ? { id: task.assignee.id, name: task.assignee.name }
    : { id: null, name: t('CONVERSATION.REPLYBOX.TASK_FORM.ALL_ASSIGNEES') };
  editNote.value = task.note || '';
  isEditModalOpen.value = true;
};

const closeEditModal = () => {
  isEditModalOpen.value = false;
  editingTask.value = null;
};

const saveEditedTask = async () => {
  if (!canSaveEdit.value) return;
  isSavingEdit.value = true;

  try {
    await ConversationApi.updateTask({
      conversationId: editingTask.value.conversation.id,
      dueAt: new Date(editDueAt.value).toISOString(),
      note: editNote.value.trim(),
      assigneeId: editAssignee.value?.id ?? null,
    });
    closeEditModal();
    await fetchTasks();
    emitter.emit('conversation:tasks_updated');
    useAlert(t('CHAT_LIST.TODAY_TASKS.EDIT_SUCCESS'));
  } catch (error) {
    useAlert(t('CHAT_LIST.TODAY_TASKS.EDIT_ERROR'));
  } finally {
    isSavingEdit.value = false;
  }
};

const deleteTask = async task => {
  const confirmed = await confirmDialog.value.showConfirmation();
  if (!confirmed) return;

  try {
    await ConversationApi.deleteTask(task.conversation.id);
    await fetchTasks();
    emitter.emit('conversation:tasks_updated');
    useAlert(t('CHAT_LIST.TODAY_TASKS.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('CHAT_LIST.TODAY_TASKS.DELETE_ERROR'));
  }
};

const onTasksUpdated = () => {
  fetchTasks();
};

onMounted(() => {
  fetchTasks();
  emitter.on('conversation:tasks_updated', onTasksUpdated);
});

onBeforeUnmount(() => {
  emitter.off('conversation:tasks_updated', onTasksUpdated);
});
</script>

<template>
  <div
    v-if="isInline"
    class="sticky top-0 z-10 border-b border-n-weak bg-n-solid-1/95 px-2 pb-2 pt-2 backdrop-blur"
  >
    <div class="rounded-xl border border-n-weak bg-n-background">
      <div
        class="flex items-center justify-between gap-3 border-b border-n-weak px-3 py-2"
      >
        <div class="min-w-0">
          <p
            class="text-[11px] font-medium uppercase tracking-wide text-n-slate-10"
          >
            {{ t('CHAT_LIST.TODAY_TASKS.OVERLINE') }}
          </p>
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('CHAT_LIST.TODAY_TASKS.TITLE') }}
          </h2>
        </div>
        <span
          v-if="tasks.length"
          class="shrink-0 rounded-full bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-11"
        >
          {{ tasks.length }}
        </span>
      </div>

      <div v-if="tasks.length" class="max-h-56 space-y-2 overflow-y-auto p-2">
        <button
          v-for="task in tasks"
          :key="task.id"
          type="button"
          class="w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-left transition-colors hover:bg-n-alpha-2"
          @click="openConversation(task.conversation)"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="text-sm font-medium truncate text-n-slate-12">
                {{
                  task.conversation.contact_name ||
                  t('CHAT_LIST.TODAY_TASKS.NO_TITLE')
                }}
              </p>
              <p class="mt-0.5 text-xs text-n-slate-10">
                {{
                  t('CHAT_LIST.TODAY_TASKS.CONVERSATION_ID', {
                    id: task.conversation.display_id,
                  })
                }}
              </p>
              <p class="mt-1 line-clamp-2 text-xs text-n-slate-11">
                {{ task.note || t('CHAT_LIST.TODAY_TASKS.NO_NOTE') }}
              </p>
            </div>
            <div class="flex shrink-0 items-center gap-2">
              <span
                class="text-xs font-medium"
                :class="
                  isOverdueTask(task.due_at)
                    ? 'text-n-ruby-11'
                    : 'text-n-blue-text'
                "
              >
                {{ formatTaskTime(task.due_at) }}
              </span>
              <button
                type="button"
                class="grid size-6 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
                :title="t('CHAT_LIST.TODAY_TASKS.EDIT')"
                @click.stop="openEditTask(task)"
              >
                <span class="i-lucide-pencil size-3.5" />
              </button>
              <button
                v-if="isAdmin"
                type="button"
                class="grid size-6 place-content-center rounded-full text-n-slate-11 hover:bg-n-ruby-3 hover:text-n-ruby-11"
                :title="t('CHAT_LIST.TODAY_TASKS.DELETE')"
                @click.stop="deleteTask(task)"
              >
                <span class="i-lucide-trash-2 size-3.5" />
              </button>
              <button
                type="button"
                class="grid size-6 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
                @click.stop="completeTask(task)"
              >
                <span class="i-lucide-check size-3.5" />
              </button>
            </div>
          </div>
        </button>
      </div>

      <div
        v-else-if="!isLoading"
        class="px-4 py-3 text-sm text-center text-n-slate-10"
      >
        {{ t('CHAT_LIST.TODAY_TASKS.EMPTY') }}
      </div>
    </div>
  </div>

  <aside
    v-else
    class="flex h-full w-80 shrink-0 flex-col border-l border-n-weak bg-n-solid-1"
  >
    <div class="border-b border-n-weak px-4 py-3">
      <p class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
        {{ t('CHAT_LIST.TODAY_TASKS.OVERLINE') }}
      </p>
      <h2 class="text-sm font-medium text-n-slate-12">
        {{ t('CHAT_LIST.TODAY_TASKS.TITLE') }}
      </h2>
    </div>

    <div v-if="tasks.length" class="flex-1 space-y-2 overflow-y-auto p-3">
      <button
        v-for="task in tasks"
        :key="task.id"
        type="button"
        class="w-full rounded-xl border border-n-weak bg-n-background p-3 text-left transition-colors hover:bg-n-alpha-2"
        @click="openConversation(task.conversation)"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="text-sm font-medium truncate text-n-slate-12">
              {{
                task.conversation.contact_name ||
                t('CHAT_LIST.TODAY_TASKS.NO_TITLE')
              }}
            </p>
            <p class="mt-1 text-xs line-clamp-2 text-n-slate-11">
              {{ task.note || t('CHAT_LIST.TODAY_TASKS.NO_NOTE') }}
            </p>
          </div>
          <div class="flex shrink-0 items-center gap-2">
            <span
              class="shrink-0 text-xs font-medium"
              :class="
                isOverdueTask(task.due_at)
                  ? 'text-n-ruby-11'
                  : 'text-n-blue-text'
              "
            >
              {{ formatTaskTime(task.due_at) }}
            </span>
            <button
              type="button"
              class="grid size-6 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
              :title="t('CHAT_LIST.TODAY_TASKS.EDIT')"
              @click.stop="openEditTask(task)"
            >
              <span class="i-lucide-pencil size-3.5" />
            </button>
            <button
              v-if="isAdmin"
              type="button"
              class="grid size-6 place-content-center rounded-full text-n-slate-11 hover:bg-n-ruby-3 hover:text-n-ruby-11"
              :title="t('CHAT_LIST.TODAY_TASKS.DELETE')"
              @click.stop="deleteTask(task)"
            >
              <span class="i-lucide-trash-2 size-3.5" />
            </button>
            <button
              type="button"
              class="grid size-6 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
              @click.stop="completeTask(task)"
            >
              <span class="i-lucide-check size-3.5" />
            </button>
          </div>
        </div>
        <div
          class="mt-3 flex items-center justify-between text-xs text-n-slate-10"
        >
          <span>
            {{
              t('CHAT_LIST.TODAY_TASKS.CONVERSATION_ID', {
                id: task.conversation.display_id,
              })
            }}
          </span>
          <span v-if="task.assignee?.name">{{ task.assignee.name }}</span>
        </div>
      </button>
    </div>

    <div
      v-else-if="!isLoading"
      class="flex flex-1 items-center justify-center px-6 text-center text-sm text-n-slate-10"
    >
      {{ t('CHAT_LIST.TODAY_TASKS.EMPTY') }}
    </div>
  </aside>

  <woot-modal v-model:show="isEditModalOpen" :on-close="closeEditModal">
    <div class="flex h-auto flex-col overflow-auto">
      <woot-modal-header :header-title="t('CHAT_LIST.TODAY_TASKS.EDIT')" />
      <div class="flex flex-col gap-4 px-6 pb-6">
        <TaskComposer
          v-if="editingTask"
          v-model:due-at="editDueAt"
          v-model:assignee="editAssignee"
          :agents="agentList"
        />
        <textarea
          v-model="editNote"
          rows="4"
          class="resize-none text-sm"
          :placeholder="t('CONVERSATION.REPLYBOX.TASK_FORM.NOTE_PLACEHOLDER')"
        />
        <div class="flex justify-end gap-2">
          <NextButton
            faded
            slate
            type="button"
            :label="t('CHAT_LIST.TODAY_TASKS.CANCEL')"
            @click="closeEditModal"
          />
          <NextButton
            type="button"
            :label="t('CHAT_LIST.TODAY_TASKS.SAVE')"
            :disabled="!canSaveEdit"
            :is-loading="isSavingEdit"
            @click="saveEditedTask"
          />
        </div>
      </div>
    </div>
  </woot-modal>

  <woot-confirm-modal
    ref="confirmDialog"
    :title="t('CHAT_LIST.TODAY_TASKS.DELETE_CONFIRM.TITLE')"
    :description="t('CHAT_LIST.TODAY_TASKS.DELETE_CONFIRM.MESSAGE')"
  />
</template>
